import { Locale } from "@/interfaces/locale";
import { Location } from "@/interfaces/location";
import SecurityPinService from "@/plugins/security-pin/security-pin.service";
import { traceabilityService } from "@/services/traceability.service";
import { AxiosResponse } from "axios";
import axios from "axios";
import HttpService from "./http.service";
import { messagesService } from "./messages.service";

class LocationsService extends HttpService {
  protected loadDispatcher: string = "LocationModule/loadLocations";
  protected defaultSort: string = "";
  protected uri = `/locations`;

  public async getLocales() {
    const url = "/locales?no_pagination";
    const response = await axios.get(url);
    return response.data.data.map((locale: Locale) => {
      return {
        id: locale.id,
        name: locale.name
      };
    });
  }

  /**
   * Create a new location
   * @param locationModel
   * @return Promise<any> (Should return a Location)
   */
  public async postLocation(locationModel: Location): Promise<any> {
    return super.post(locationModel);
  }

  /**
   * Get a list of locations
   * @param query
   */
  public async getLocations(query?: {
    [key: string]: string | boolean | string[] | number[];
  }): Promise<Location[]> {
    this.query.embed = "user,state";
    const response = await super.get(query);
    return response.data.data;
  }

  public async put(model: Location, data: Location): Promise<any> {
    return super.put(model, data);
  }

  /**
   * Deletes a specific location by its id after pin validation
   * @param model Location
   */
  public async deleteLocation(model: Location) {
    const pinService: SecurityPinService = this.$services.inject(
      SecurityPinService,
      this
    );
    try {
      const pin: string = await pinService.ensure(
        "security_pin.title",
        "security_pin.description"
      );
      const traceability = await traceabilityService.getCurrentTraceability({
        location_id: model.id
      });
      const hasTraceability = traceability && !!traceability.length;
      return await this.deleteRequest(model, pin).then(response => {
        if (hasTraceability) {
          traceabilityService.deleteTraceability(model.id);
        }
        return response;
      });
    } catch (e) {
      // Nothing to do
    }
  }

  /**
   * Deletes a location given a pin is provided
   * @param model
   * @param pin
   */
  public async deleteRequest(model: Location, pin: string) {
    try {
      const response: AxiosResponse = await this.axios({
        method: "DELETE",
        url: `/locations/${model.id}`,
        headers: {
          Pincode: `${pin}`
        }
      });
      return response;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      throw Error();
    }
  }

  /**
   * Find a specific location by its id
   * @param id
   */
  public async find(id: number): Promise<Location | null> {
    return await super.findById(id, { embed: "state" });
  }

  /**
   * Search a location my a partial of its name
   * @param locationName
   */
  public async searchLocation(locationName: string): Promise<Location[]> {
    this.query["q[name_contains]"] = locationName;
    return this.getLocations();
  }

  /**
   * Returns all locations without pagination
   */
  public async getAll() {
    try {
      const response = await super.get({ no_pagination: true });
      return response.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }
}

export const locationsService: LocationsService = new LocationsService();
